/**
 * Номер велика — единственная идентичность (см. scout-rewrite-spec.md §1).
 *
 * Нормализация зеркалит контракт C1 (`GET /v1/scout/vehicles/by-number/{n}`):
 * регистр и пробелы/дефисы игнорируются, ввод только из цифр совпадает с
 * токенами, у которых хвостовые цифры дают то же число («1» ≡ «001» ≡
 * «Aida001»), точное совпадение приоритетнее числового.
 *
 * Живёт здесь, а не в `api/vehicles.ts`, по той же причине, что и
 * `describe-bike.ts`: это лист без сети, нужный и офлайн-резолву (сначала
 * ищем в уже загруженном парке — см. `lib/scanner/resolve.ts`), и
 * серверному фолбэку (`api/vehicles.ts` вызывает `findByNumber` над кэшем
 * ПЕРЕД тем, как идти на `by-number`, и снова над кэшем, если ручка ответит
 * 404 — маршрут ещё не выкачен).
 */

export type NumberedBike = { id: string; number: string }

/** Регистр и пробелы/дефисы — не часть номера. */
export function normalizeNumberExact(raw: string): string {
  return raw.trim().toLowerCase().replace(/[\s-]+/g, '')
}

function isAllDigits(s: string): boolean {
  return s.length > 0 && /^[0-9]+$/.test(s)
}

/** Хвостовые цифры нормализованного токена, либо null, если их нет. */
function trailingDigits(normalized: string): string | null {
  const m = /([0-9]+)$/.exec(normalized)
  return m?.[1] ?? null
}

/**
 * Кандидаты по номеру. Пустой список — не найдено. Больше одного —
 * неоднозначность (контракт C1: 409 VEHICLE_NUMBER_AMBIGUOUS).
 *
 * Точное совпадение (после нормализации регистра/пробелов/дефисов) всегда
 * побеждает числовое: если найден хоть один точный кандидат, числовой
 * фолбэк не считается вовсе.
 */
export function findByNumber<T extends NumberedBike>(
  input: string,
  bikes: readonly T[],
): T[] {
  const norm = normalizeNumberExact(input)
  if (norm === '') return []

  const exact = bikes.filter((b) => normalizeNumberExact(b.number) === norm)
  if (exact.length > 0) return exact

  if (!isAllDigits(norm)) return []
  const wanted = Number.parseInt(norm, 10)
  return bikes.filter((b) => {
    const digits = trailingDigits(normalizeNumberExact(b.number))
    return digits !== null && Number.parseInt(digits, 10) === wanted
  })
}

/**
 * Короткая подпись для метки на карте: 30 px не вмещают «Aida017» читаемо,
 * а хвостовые цифры («17») — это ровно то, чем скаут и называет байк вслух.
 * Номер без цифр (нестандартная наклейка) обрезается с конца: последние
 * символы отличают наклейки друг от друга чаще, чем первые.
 */
export function shortLabel(number: string): string {
  if (!number) return '?'
  const digits = trailingDigits(number.toLowerCase())
  if (digits) return String(Number.parseInt(digits, 10))
  return number.length <= 4 ? number : number.slice(-4)
}

/**
 * Порядок по номеру — как скаут читает список кучи: 9, 41, 120, а не
 * строкой «120» раньше «41». Номера без цифр — в конец.
 */
export function compareNumbers(a: string, b: string): number {
  const da = trailingDigits(a.toLowerCase())
  const db = trailingDigits(b.toLowerCase())
  if (da !== null && db !== null) {
    const diff = Number.parseInt(da, 10) - Number.parseInt(db, 10)
    if (diff !== 0) return diff
  } else if (da !== null) {
    return -1
  } else if (db !== null) {
    return 1
  }
  return a < b ? -1 : a > b ? 1 : 0
}

/** «1 велик», «3 велика», «5 великов» — подпись кучи и базы на карте. */
export function bikesCount(n: number): string {
  const d10 = n % 10
  const d100 = n % 100
  const word =
    d10 === 1 && d100 !== 11
      ? 'велик'
      : d10 >= 2 && d10 <= 4 && (d100 < 12 || d100 > 14)
        ? 'велика'
        : 'великов'
  return `${n} ${word}`
}
